import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";

import { Section, SectionHeading } from "../common/Section";
import Reveal from "../common/Reveal";
import {
  SERVICES,
  SERVICE_CATEGORIES,
  technologyIndex,
} from "../../data/serviceCatalog";

/**
 * Technical credibility section.
 *
 * Every technology name below is copied from src/data/serviceCatalog.js,
 * which is generated from the four real service data files. Nothing here
 * is invented: the grouping into disciplines is presentation, the names
 * and the service links underneath them are data. The counts printed in
 * the header are derived from the catalog at import time, so they cannot
 * drift away from the services that actually exist.
 */

/** The owner's capability list. Each one maps to real services in the catalog. */
const CAPABILITIES = [
  "Custom Software Development",
  "Web Application Development",
  "Full-Stack Development",
  ".NET Development",
  "React/Next.js Development",
  "API Development & Integrations",
  "ERP Systems",
  "Database Development",
  "Payment Gateway Integration",
  "Business Automation",
  "UI/UX Design",
  "Maintenance & Support",
];

/**
 * Disciplines. `tech` members are verified verbatim against the 114 distinct
 * technology strings in the catalog — if a name is not listed on a real
 * service page it does not appear here.
 */
const DISCIPLINE_SOURCE = [
  {
    key: "frontend",
    name: "Frontend & mobile",
    note:
      "The layer people actually touch: web front ends, cross-platform apps, and native builds for iOS and Android.",
    tech: [
      "React",
      "Next.js",
      "Vue.js",
      "TypeScript",
      "JavaScript",
      "Redux",
      "React Native",
      "Flutter",
      "iOS Swift",
      "Android Kotlin",
    ],
  },
  {
    key: "backend",
    name: "Backend & APIs",
    note:
      "Application logic, service layers and the interfaces other systems call. Node.js, .NET, Java and Python, because the right runtime depends on what already runs in the business.",
    tech: [
      "Node.js",
      ".NET Core",
      "Java Spring",
      "Python",
      "Python Django",
      "REST APIs",
      "GraphQL",
      "Apex",
      "XML-RPC",
      "Firebase",
    ],
  },
  {
    key: "data",
    name: "Data & analytics",
    note:
      "Schemas, queries and pipelines: relational and document stores, caching, streaming, and the reporting layer that sits on top of them.",
    tech: [
      "PostgreSQL",
      "MongoDB",
      "Redis",
      "SQL",
      "Azure SQL",
      "Dataverse",
      "Apache Kafka",
      "Apache Spark",
      "Power BI",
      "Tableau",
    ],
  },
  {
    key: "cloud",
    name: "Cloud & DevOps",
    note:
      "Where a system runs and how it keeps running: containers, orchestration, two clouds, and the monitoring and backup work that starts on launch day.",
    tech: [
      "AWS",
      "Azure",
      "Docker",
      "Kubernetes",
      "Heroku",
      "CDN",
      "SSL Certificates",
      "Monitoring Tools",
      "Backup Systems",
      "Security Scanners",
    ],
  },
  {
    key: "ai",
    name: "AI & machine learning",
    note:
      "Models, retrieval and conversational agents built into an existing workflow rather than bolted on beside it.",
    tech: [
      "TensorFlow",
      "PyTorch",
      "AWS SageMaker",
      "Azure ML",
      "Google AI",
      "OpenAI",
      "LangChain",
      "Dialogflow",
      "Rasa",
      "Azure Bot Service",
      "DALL-E",
      "Stable Diffusion",
    ],
  },
  {
    key: "platforms",
    name: "Business platforms",
    note:
      "ERP and CRM work on the platforms a company already licenses: configuration, extension, and getting them to talk to everything else.",
    tech: [
      "Microsoft Dynamics 365",
      "Power Platform",
      "Power Apps",
      "Power Automate",
      "Microsoft 365",
      "Sales Cloud",
      "Service Cloud",
      "Lightning",
      "MuleSoft",
      "Odoo",
    ],
  },
  {
    key: "integrations",
    name: "Integrations",
    note:
      "Payments, messaging channels and workflow automation: the connections that make separate systems behave like one.",
    tech: [
      "Stripe",
      "Payment Gateways",
      "WhatsApp API",
      "Telegram API",
      "Instagram API",
      "TikTok API",
      "Zapier",
      "Make.com",
      "CRM Integration",
    ],
  },
];

/**
 * Attach the real services that list each discipline's technologies,
 * ranked by how many of them they use. Computed once at module load
 * (25 services x 7 disciplines), so it never re-runs on render.
 */
const DISCIPLINES = DISCIPLINE_SOURCE.map((discipline) => {
  const wanted = new Set(discipline.tech);
  const services = SERVICES.map((service) => ({
    service,
    hits: service.technologies.filter((tech) => wanted.has(tech)).length,
  }))
    .filter((entry) => entry.hits > 0)
    .sort((a, b) => b.hits - a.hits)
    .slice(0, 4)
    .map((entry) => entry.service);

  return { ...discipline, services };
});

const TECH_TOTAL = technologyIndex().length;

/**
 * How many of the real services list technologies from more than one
 * discipline. Derived from the catalog for the same reason the counts in
 * the header are: a sentence about the shape of the work has to be
 * countable rather than asserted.
 */
const MULTI_DISCIPLINE_SERVICES = SERVICES.filter(
  (service) =>
    DISCIPLINE_SOURCE.filter((discipline) =>
      service.technologies.some((tech) => discipline.tech.includes(tech))
    ).length > 1
).length;

const pad = (n) => String(n + 1).padStart(2, "0");

const TechnologyStack = () => {
  const [active, setActive] = useState(0);
  const tabRefs = useRef([]);

  const discipline = DISCIPLINES[active];

  // Roving tabindex: arrows move between disciplines, Home/End jump to the
  // ends. Both axes are handled because the rail is a grid on small screens
  // and a vertical list on large ones.
  const handleKeyDown = (event) => {
    const last = DISCIPLINES.length - 1;
    let next = null;

    if (event.key === "ArrowDown" || event.key === "ArrowRight") {
      next = active === last ? 0 : active + 1;
    } else if (event.key === "ArrowUp" || event.key === "ArrowLeft") {
      next = active === 0 ? last : active - 1;
    } else if (event.key === "Home") {
      next = 0;
    } else if (event.key === "End") {
      next = last;
    }

    if (next === null) return;
    event.preventDefault();
    setActive(next);
    const node = tabRefs.current[next];
    if (node) node.focus();
  };

  const stats = [
    { value: TECH_TOTAL, label: "technologies named" },
    { value: SERVICES.length, label: "services" },
    { value: SERVICE_CATEGORIES.length, label: "service categories" },
  ];

  return (
    <Section tone="dark" id="technology" className="grid-field-dark">
      {/* Argument + evidence counts */}
      <div className="grid gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:items-end lg:gap-14">
        <SectionHeading
          tone="dark"
          maxWidth="max-w-xl"
          eyebrow="Engineering stack"
          title="We build systems, not just websites."
          lead={`Every technology below is named on one of our ${SERVICES.length} service pages, so this is the working stack rather than a wish list, from a React front end through to a Dynamics 365 rollout. ${MULTI_DISCIPLINE_SERVICES} of those services draw on more than one of these disciplines.`}
        />

        <Reveal delay={90}>
          <dl className="grid grid-cols-3 gap-px overflow-hidden rounded-card border border-white/10 bg-white/10">
            {stats.map((stat) => (
              // column-reverse so the number reads first while the DOM keeps
              // the dt/dd order a definition list needs.
              <div
                key={stat.label}
                className="flex flex-col-reverse bg-ink-950 px-4 py-5 sm:px-5"
              >
                <dt className="mt-1.5 text-[11px] leading-tight text-ink-400">
                  {stat.label}
                </dt>
                <dd className="font-mono text-2xl tabular-nums text-white sm:text-3xl">
                  {stat.value}
                </dd>
              </div>
            ))}
          </dl>
          <p className="mt-4 max-w-sm text-[13px] leading-relaxed text-ink-400">
            Counted from the service pages on this site, not estimated.
          </p>
        </Reveal>
      </div>

      {/* Discipline rail + panel */}
      <Reveal delay={60} className="mt-12 sm:mt-16">
        <div className="grid gap-6 lg:grid-cols-[minmax(0,16rem)_minmax(0,1fr)] lg:gap-10">
          <div
            role="tablist"
            aria-label="Engineering disciplines"
            className="grid grid-cols-2 self-start border-t border-white/10 lg:grid-cols-1"
          >
            {DISCIPLINES.map((item, index) => {
              const isActive = index === active;
              return (
                <button
                  key={item.key}
                  type="button"
                  role="tab"
                  id={`stack-tab-${item.key}`}
                  aria-selected={isActive}
                  aria-controls="stack-panel"
                  tabIndex={isActive ? 0 : -1}
                  ref={(node) => {
                    tabRefs.current[index] = node;
                  }}
                  onClick={() => setActive(index)}
                  onKeyDown={handleKeyDown}
                  className={`flex items-center gap-3 border-b px-2 py-3.5 text-left transition-[background-color,border-color,color] duration-200 sm:px-3 ${
                    isActive
                      ? "border-brand-400/60 bg-white/[0.05]"
                      : "border-white/10 hover:bg-white/[0.03]"
                  }`}
                >
                  <span
                    aria-hidden="true"
                    className={`font-mono text-[11px] tabular-nums ${
                      isActive ? "text-brand-300" : "text-ink-400"
                    }`}
                  >
                    {pad(index)}
                  </span>
                  <span
                    className={`text-[13px] font-medium leading-snug sm:text-sm ${
                      isActive ? "text-white" : "text-ink-300"
                    }`}
                  >
                    {item.name}
                  </span>
                  <span
                    aria-hidden="true"
                    className="ml-auto hidden font-mono text-[11px] tabular-nums text-ink-400 lg:inline"
                  >
                    {item.tech.length}
                  </span>
                </button>
              );
            })}
          </div>

          <div
            id="stack-panel"
            role="tabpanel"
            aria-labelledby={`stack-tab-${discipline.key}`}
            className="rounded-panel border border-white/10 bg-white/[0.03] p-5 sm:p-8 lg:min-h-[26rem]"
          >
            {/* Re-keyed so switching disciplines settles in rather than snapping. */}
            <div
              key={discipline.key}
              style={{ animation: "smoothFadeIn 300ms var(--dl-ease) both" }}
            >
              <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                <h3 className="font-bricolage text-xl font-semibold text-white sm:text-2xl">
                  {discipline.name}
                </h3>
                <p className="font-mono text-[11px] uppercase tracking-eyebrow text-ink-400">
                  {discipline.tech.length} technologies
                </p>
              </div>

              <p className="mt-3 max-w-[58ch] text-sm leading-relaxed text-ink-300 sm:text-[15px]">
                {discipline.note}
              </p>

              <ul className="mt-7 grid grid-cols-2 gap-x-5 sm:grid-cols-3 sm:gap-x-8 xl:grid-cols-4">
                {discipline.tech.map((tech) => (
                  <li
                    key={tech}
                    className="border-t border-white/10 py-2.5 font-mono text-[12.5px] leading-snug text-ink-200"
                  >
                    {tech}
                  </li>
                ))}
              </ul>

              <div className="mt-8 border-t border-white/10 pt-6">
                <p className="font-mono text-[11px] uppercase tracking-eyebrow text-ink-400">
                  Services that use it
                </p>
                <ul className="mt-3 flex flex-wrap gap-x-6 gap-y-2">
                  {discipline.services.map((service) => (
                    <li key={service.path}>
                      <Link
                        to={service.path}
                        className="group inline-flex items-center gap-1.5 border-b border-transparent pb-0.5 text-sm text-ink-200 transition-[color,border-color] duration-200 hover:border-brand-400 hover:text-white"
                      >
                        {service.title}
                        <ArrowUpRight
                          aria-hidden="true"
                          className="h-3.5 w-3.5 shrink-0 text-ink-400 transition-colors duration-200 group-hover:text-brand-300"
                        />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </Reveal>

      {/* What the stack is used for, in the owner's own words */}
      <Reveal delay={60} className="mt-14 border-t border-white/10 pt-8 sm:mt-16">
        <div className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h3 className="font-bricolage text-lg font-semibold text-white sm:text-xl">
              What we build with it
            </h3>
            <p className="mt-2 max-w-[52ch] text-sm leading-relaxed text-ink-400">
              Twelve capability areas, delivered across the four service
              categories.
            </p>
          </div>
          <Link to="/services" className="btn-secondary shrink-0 self-start sm:self-auto">
            Browse all {SERVICES.length} services
            <ArrowRight aria-hidden="true" className="h-4 w-4" />
          </Link>
        </div>

        <ul className="mt-7 grid grid-cols-1 gap-x-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {CAPABILITIES.map((capability, index) => (
            <li
              key={capability}
              className="flex items-baseline gap-3 border-t border-white/10 py-2.5 text-sm text-ink-300"
            >
              <span
                aria-hidden="true"
                className="font-mono text-[11px] tabular-nums text-ink-400"
              >
                {pad(index)}
              </span>
              <span>{capability}</span>
            </li>
          ))}
        </ul>
      </Reveal>
    </Section>
  );
};

export default TechnologyStack;
